import React, { useState, useMemo } from 'react';
import cx from 'classnames';

import { Icon, TruncatedLabel } from '../../../../../ui-kit';
import FolderIcon from '../../FolderIcon';
import { buildTagTree, matchNodesForSearch, ancestorPaths, toggleInSet } from '../../../utils';
import '../styles/_folder-tree-menu.scss';

// Self-contained, collapsible folder tree for the "Add to folder" context menu.
// It owns its expand/collapse, search and (optimistic) tagged state locally so
// interactions never trigger the parent menu's option rebuild. Visual design
// mirrors the media-library sidebar tree.
const FolderTreeMenu = ({ tags = [], taggedTagIds = [], onToggleTag, onCreateFolder }) => {
  const [tagged, setTagged] = useState(() => new Set(taggedTagIds));
  const [search, setSearch] = useState('');
  const [expandedPaths, setExpandedPaths] = useState(() => {
    // Auto-expand the branch down to every folder the asset is already in.
    const next = new Set();
    tags.forEach((t) => {
      if (taggedTagIds.includes(t.tagId)) {ancestorPaths(t.label).forEach((p) => next.add(p));}
    });
    return next;
  });

  const tree = useMemo(() => buildTagTree(tags), [tags]);
  const visiblePaths = search ? matchNodesForSearch(tags, search) : null;
  const effectiveExpanded = search ? visiblePaths : expandedPaths;

  const toggleExpand = (path, e) => {
    e.stopPropagation();
    setExpandedPaths((prev) => toggleInSet(prev, path));
  };

  const handleToggleTag = (node) => {
    if (!node.tagId) {return;}
    setTagged((prev) => toggleInSet(prev, node.tagId));
    if (onToggleTag) {onToggleTag(node.tag);}
  };

  const renderNode = (node) => {
    if (visiblePaths && !visiblePaths.has(node.path)) {return null;}

    const hasChildren = node.children.length > 0;
    const isExpanded = effectiveExpanded.has(node.path);
    const isTagged = Boolean(node.tagId) && tagged.has(node.tagId);

    return (
      <React.Fragment key={node.path}>
        <div
          className={cx('folder-tree-menu__row', {
            'folder-tree-menu__row--active': isTagged,
            'folder-tree-menu__row--placeholder': node.isPlaceholder
          })}
          onClick={(e) => {
            if (node.isPlaceholder) {
              if (hasChildren) {toggleExpand(node.path, e);}
              return;
            }
            handleToggleTag(node);
          }}
          onKeyDown={(e) => e.key === 'Enter' && !node.isPlaceholder && handleToggleTag(node)}
          role="button"
          tabIndex={0}>

          {Array.from({ length: node.depth }).map((unused, i) =>
          <span key={i} className="folder-tree-menu__guide" />
          )}

          <FolderIcon className="folder-tree-menu__icon" />
          <TruncatedLabel
            id={`folder-tree-menu-name-${encodeURIComponent(node.path)}`}
            text={node.segment}
            className="folder-tree-menu__name" />

          {isTagged && <span className="folder-tree-menu__check">✓</span>}

          {hasChildren &&
          <span
            className="folder-tree-menu__chevron"
            onClick={(e) => toggleExpand(node.path, e)}
            onKeyDown={(e) => {if (e.key === 'Enter') {toggleExpand(node.path, e);}}}
            role="button"
            tabIndex={0}
            title={isExpanded ? 'Collapse' : 'Expand'}>

            <Icon name={isExpanded ? 'chevrondown' : 'chevronright'} size={12} color="regent" />
          </span>}
        </div>

        {isExpanded && node.children.map(renderNode)}
      </React.Fragment>);

  };

  return (
    <div className="folder-tree-menu" onClick={(e) => e.stopPropagation()} role="presentation">
      <div className="folder-tree-menu__search">
        <Icon name="search" size={14} color="regent" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onKeyPress={(e) => e.stopPropagation()}
          onFocus={(e) => e.stopPropagation()}
          placeholder="Search folders..."
          className="folder-tree-menu__search-input" />

      </div>

      <div className="folder-tree-menu__list">
        {tree.roots.length > 0 ?
        tree.roots.map(renderNode) :
        <div className="folder-tree-menu__empty">
            <span className="folder-tree-menu__empty-text">No folders available</span>
            <button type="button" className="folder-tree-menu__create" onClick={onCreateFolder}>
              + Create Folder
            </button>
          </div>}
      </div>
    </div>);

};

export default FolderTreeMenu;
